import { useCallback, useMemo } from "react";
import { useSetAtom as baseUseSetAtom } from "jotai";
import { DrawerRefKey } from "../utils/types";
import {
  ClientInformationDrawerTab,
  ProviderDrawerState,
  drawerStateAtom,
  treatmentPlanContentStatus,
} from "../../state";
import { activeClientInformationTabAtom } from "../state";
import { useTrackEvent as baseUseTrackEvent } from "../../../segment/segment";
import { EventTypeEnum } from "../../../segment/types";
import {
  useSetActiveClientInfoDrawerTab as baseUseSetActiveClientInfoDrawerTab,
  ClientInformationDrawerTab as SeedlingClientInformationDrawerTab,
  TreatmentPlanStatus,
  useDrawerRefsContext as baseUseDrawerRefsContext,
  type ResourceButtonOnClickProps,
} from "@grow-therapy-team/seedling-components";
import { useAtomCallback as baseUseAtomCallback } from "jotai/utils";
import { roomAtom } from "../../../video-sdk";
import { logger } from "../../../datadog";

export type UseResourceButtonOnClickHandlersDependencies = {
  useAtomCallback: typeof baseUseAtomCallback;
  useDrawerRefsContext: typeof baseUseDrawerRefsContext;
  useSetActiveClientInfoDrawerTab: typeof baseUseSetActiveClientInfoDrawerTab;
  useSetAtom: typeof baseUseSetAtom;
  useTrackEvent: typeof baseUseTrackEvent;
};

/**
 * Wrapper function that can invoke resource button `onClick` handlers with
 * explicit control, for example to do initial setup work like opening the
 * drawer first, or to conditionally perform handler logic.
 */
export type PreHandler = (fns: {
  setState: () => void;
  scrollAndFocus?: () => void;
}) => void;

/**
 * Dummy pre-handler that does nothing special, just invokes the provided
 * handler functions in sequence. Useful as a default.
 */
const NOOP_PRE_HANDLER: PreHandler = ({ setState, scrollAndFocus }) => {
  setState();
  scrollAndFocus?.();
};

export interface UseResourceButtonOnClickHandlersProps {
  appointmentShortId?: string;
  /**
   * Optional callback to invoke the handle functions with explicit control, for
   * example to do initial setup work like opening the drawer first, or to
   * conditionally perform handler logic.
   *
   * @example
   * ```ts
   * const preHandler = ({ setState, scrollAndFocus }) => {
   *   // Do initial setup work
   *   doSetupWork();
   *   // Call an actual handler function
   *   setState();
   *   // Conditionally invoke handler function
   *   if (someCondition) scrollAndFocus?.();
   * };
   */
  preHandler?: PreHandler;
  providerShortId?: string;
  /**
   * Optional dependencies to use for the hook. Useful for testing.
   */
  dependencies?: UseResourceButtonOnClickHandlersDependencies;
}

/**
 * Hook that provides onClick handlers for pre-visit summary resource buttons.
 * These handlers navigate to the appropriate tab in the client info drawer and
 * scroll/focus the relevant card.
 *
 * Supply a `preHandler` callback prop for fine-grained control over the handler
 * execution.
 */
export function useResourceButtonOnClickHandlers({
  appointmentShortId,
  preHandler = NOOP_PRE_HANDLER,
  providerShortId,
  dependencies = {
    useAtomCallback: baseUseAtomCallback,
    useDrawerRefsContext: baseUseDrawerRefsContext,
    useSetActiveClientInfoDrawerTab: baseUseSetActiveClientInfoDrawerTab,
    useSetAtom: baseUseSetAtom,
    useTrackEvent: baseUseTrackEvent,
  },
}: UseResourceButtonOnClickHandlersProps): ResourceButtonOnClickProps {
  const {
    useAtomCallback,
    useDrawerRefsContext,
    useSetActiveClientInfoDrawerTab,
    useSetAtom,
    useTrackEvent,
  } = dependencies;

  const { getRef } = useDrawerRefsContext<DrawerRefKey>();
  // NOTE: setActiveTab is only consumed by the old drawer body. We double-write
  // to both the old atom and the Seedling tab setter to support the feature flag
  // `shouldUseSeedlingClientInfoDrawer`. Once the FF is fully released and the
  // old code path is removed, we can remove the setActiveTab call.
  const setActiveTab = useSetAtom(activeClientInformationTabAtom);
  const setSeedlingActiveTab = useSetActiveClientInfoDrawerTab();
  const setDrawerState = useSetAtom(drawerStateAtom);
  const setTreatmentPlanContentStatus = useSetAtom(treatmentPlanContentStatus);
  const getIsInSession = useAtomCallback(
    useCallback((get) => !!get(roomAtom), []),
  );
  const { userClicked } = useTrackEvent();

  const recordButtonClickEvent = useCallback(
    (buttonName: string) => {
      // Realistically, these should always be available, but if they're missing
      // for some reason, we can't effectively track the event.
      if (!appointmentShortId || !providerShortId) {
        logger.error(
          "Missing appointmentShortId or providerShortId when recording PSP resource button click event",
          { appointmentShortId, cta: buttonName, providerShortId },
        );
        return;
      }
      userClicked(EventTypeEnum.PROVIDER_PRE_SESSION_PREP_BUTTON_CLICKED, {
        cta: buttonName,
        appointmentShortId,
        providerShortId,
        inSession: getIsInSession(),
      });
    },
    [appointmentShortId, getIsInSession, providerShortId, userClicked],
  );

  const scrollAndFocusRef = useCallback(
    (key: DrawerRefKey) => {
      // Poll for the ref with retries since the drawer/tab content may not be
      // rendered yet if the drawer was just opened and its data has never been
      // fetched before. We use exponential backoff with a reasonable timeout.
      const pollForRef = (attempt = 0, maxAttempts = 5, delay = 50) => {
        const ref = getRef(key);
        if (ref?.current) {
          ref.current.scrollIntoView({ block: "nearest" });
          ref.current.focus();
        } else if (attempt < maxAttempts) {
          // Retry with exponential backoff (50ms, 100ms, 200ms, etc.)
          setTimeout(
            () => pollForRef(attempt + 1, maxAttempts, delay * 2),
            delay,
          );
        }
      };

      // Start polling after allowing for first render cycle to complete.
      setTimeout(() => pollForRef(), 0);
    },
    [getRef],
  );

  const setMeasuresDrawer = useCallback(() => {
    recordButtonClickEvent("measures");
    setActiveTab(ClientInformationDrawerTab.TREATMENT);
    setSeedlingActiveTab(SeedlingClientInformationDrawerTab.TREATMENT);
  }, [recordButtonClickEvent, setActiveTab, setSeedlingActiveTab]);

  const setClientActivityDrawer = useCallback(() => {
    recordButtonClickEvent("client-activity");
    // For the old drawer, client activity is in the INFORMATION tab
    setActiveTab(ClientInformationDrawerTab.INFORMATION);
    // For the Seedling drawer, client activity has its own tab
    setSeedlingActiveTab(SeedlingClientInformationDrawerTab.CLIENT_ACTIVITY);
  }, [recordButtonClickEvent, setActiveTab, setSeedlingActiveTab]);

  const setProgressNoteDrawer = useCallback(() => {
    recordButtonClickEvent("progress-note");
    setDrawerState(ProviderDrawerState.PROGRESS_NOTES);
  }, [recordButtonClickEvent, setDrawerState]);

  const setTreatmentGoalsDrawer = useCallback(() => {
    recordButtonClickEvent("treatment-goals");
    setTreatmentPlanContentStatus(TreatmentPlanStatus.CURRENT_GOALS);
    setDrawerState(ProviderDrawerState.TREATMENT_PLANS_CONTENT);
  }, [recordButtonClickEvent, setTreatmentPlanContentStatus, setDrawerState]);

  const handlers: ResourceButtonOnClickProps = useMemo(
    () => ({
      measures: () => {
        preHandler({
          setState: setMeasuresDrawer,
          scrollAndFocus: () => scrollAndFocusRef(DrawerRefKey.MEASURES_CARD),
        });
      },
      "client-activity": () => {
        preHandler({
          setState: setClientActivityDrawer,
          scrollAndFocus: () =>
            scrollAndFocusRef(DrawerRefKey.CLIENT_ACTIVITY_CARD),
        });
      },
      "progress-note": () => {
        preHandler({ setState: setProgressNoteDrawer });
      },
      "treatment-goals": () => {
        preHandler({ setState: setTreatmentGoalsDrawer });
      },
    }),
    [
      preHandler,
      scrollAndFocusRef,
      setClientActivityDrawer,
      setMeasuresDrawer,
      setProgressNoteDrawer,
      setTreatmentGoalsDrawer,
    ],
  );

  return handlers;
}
